import { Navigate } from "react-router-dom";
import { useAppContext } from "../../../AppContext";
import { ItemsList } from "../../../common/components/itemsList/ItemsList";
import { DefaultButton } from "../../../common/components/button/defaultButton/DefaultButton";
import { EmptyState } from "../../../common/components/common/EmptyState/EmptyState";
import { FilterIconButton } from "../../../common/components/filters/FilterIconButton";
import { FiltersSidePanel } from "../../../common/components/filters/FiltersSidePanel";
import { IconType } from "../../../common/icons/Icon";
import { RenderWhen } from "../../../common/infrastructure/components/RenderWhen";
import { getCurrentUserRole } from "../../../common/infrastructure/helpers/userHelper";
import { routePaths } from "../../../common/infrastructure/routes/routePaths";
import { StarRating } from "../../reviews/components/StarRating/StarRating";
import {
  canManageReviews,
  formatAverageRating,
} from "../../reviews/utils/reviewUtility";
import { ReviewListItem } from "./components/ReviewListItem";
import { ReviewReplyDialog } from "./components/ReviewReplyDialog";
import { ReviewSettingsDialog } from "./components/ReviewSettingsDialog";
import { ReviewsFiltersPanelContent } from "./components/ReviewsFiltersPanelContent";
import { useStudioReviewsPage } from "./hooks/useStudioReviewsPage";
import styles from "./StudioReviewsPage.module.scss";

const StudioReviewsContent = () => {
  const {
    reviews,
    isLoading,
    isSaving,
    averageRating,
    visibleCount,
    hiddenCount,
    ratingFilter,
    setRatingFilter,
    hiddenFilter,
    setHiddenFilter,
    activeFiltersCount,
    clearFilters,
    isFiltersOpen,
    openFilters,
    closeFilters,
    isSettingsOpen,
    openSettings,
    closeSettings,
    toggleHidden,
    replyTarget,
    replyForm,
    replyLength,
    openReply,
    closeReply,
    saveReply,
  } = useStudioReviewsPage();

  const headerActions = (
    <div className={styles.root_actions}>
      <DefaultButton
        label="Настройки"
        design="outline"
        width="auto"
        onClick={openSettings}
      />
      <FilterIconButton
        onClick={openFilters}
        activeFiltersCount={activeFiltersCount}
      />
    </div>
  );

  const noItemsContent =
    activeFiltersCount > 0 ? (
      <EmptyState
        icon={IconType.Filters}
        title="Няма ревюта по тези филтри"
        description="Опитайте с друга оценка или видимост."
        actionLabel="Изчисти филтрите"
        onAction={clearFilters}
      />
    ) : (
      <EmptyState
        icon={IconType.Star}
        title="Все още няма ревюта"
        description="До няколко часа след посещение, отбелязано като „Дошъл“, клиентите получават покана по имейл. Могат да оставят ревю и от „Моите резервации“. Ревютата ще се появят тук."
        actionLabel="Настройки на ревютата"
        onAction={openSettings}
      />
    );

  return (
    <>
      <ItemsList
        isLoading={isLoading}
        title="Ревюта"
        tooltip="Скритото ревю не се вижда от клиентите, но остава тук. Отговорът ви се показва публично под ревюто."
        noItemsText={noItemsContent}
        items={reviews}
        showItemsCount
        fullHeight
        actionButton={headerActions}
        footer={
          <RenderWhen condition={visibleCount + hiddenCount > 0}>
            <div className={styles.root_summary}>
              <span className={styles.root_summary_average}>
                {formatAverageRating(averageRating)}
              </span>
              <StarRating value={averageRating} />
              <span className={styles.root_summary_label}>
                {`средна оценка · ${visibleCount} видими · ${hiddenCount} скрити`}
              </span>
            </div>
          </RenderWhen>
        }
        swipeRightActions={[
          {
            type: "onClick",
            iconType: IconType.ChatBubbles,
            onClick: openReply,
          },
        ]}
        swipeLeftActions={[
          {
            type: "onClick",
            iconType: IconType.CrossedOutEye,
            validator: (review) => !review.isHidden,
            enablePrompt: true,
            promptMessage:
              "Да скрием ли това ревю? Клиентите няма да го виждат, но то остава тук и можете да го покажете отново.",
            onClick: toggleHidden,
          },
          {
            type: "onClick",
            iconType: IconType.PutBackIcon,
            validator: (review) => review.isHidden,
            onClick: toggleHidden,
          },
        ]}
        itemTemplate={(review, index, actionButtons, className) => (
          <ReviewListItem
            key={review?.id ?? index}
            className={className}
            review={review}
            actionButtons={actionButtons}
          />
        )}
      />

      <FiltersSidePanel visible={isFiltersOpen} onHide={closeFilters}>
        <ReviewsFiltersPanelContent
          ratingFilter={ratingFilter}
          hiddenFilter={hiddenFilter}
          onRatingChange={setRatingFilter}
          onHiddenChange={setHiddenFilter}
          onClearAll={clearFilters}
          onClose={closeFilters}
        />
      </FiltersSidePanel>

      <ReviewSettingsDialog visible={isSettingsOpen} onHide={closeSettings} />

      <ReviewReplyDialog
        review={replyTarget}
        form={replyForm}
        replyLength={replyLength}
        isSaving={isSaving}
        onSave={saveReply}
        onHide={closeReply}
      />
    </>
  );
};

/**
 * The page behind the "Ревюта" menu item. A role the API refuses (an Employee has the route
 * but is answered 401, which logs them out) lands on the home page before any request is
 * made. The reviews flag is not checked here: it hides the menu item, and flags load after
 * the first render, so checking it would send a manager home on every refresh.
 */
export const StudioReviewsPage = () => {
  const { currentUser } = useAppContext();

  if (!canManageReviews(getCurrentUserRole(currentUser))) {
    return <Navigate to={routePaths.home} replace />;
  }

  return <StudioReviewsContent />;
};
